'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createContentModerationFinding,
  fetchProvenanceFileStatus,
  provenanceKeys,
  ProvenanceApiError,
  type ContentModerationFinding,
  type DetectionType,
} from '@/shared/cors-provenance-api';
import { auditKeys } from '@/shared/cors-audit-api';
import {
  FLAG_NOT_SAVED_EXISTING_OBFUSCATION,
  isExistingObfuscationConflict,
} from '@/components/ui/media/media-flag-rules';
import { useCellServiceSession } from './useCellServiceSession';
import { useContentModerationFolderNameResolver } from './useContentModerationFolderName';
import { invalidateFileInfoCache } from '@/shared/clientFunctions';
import { markManualObfuscationInProgress } from '@/lib/manual-obfuscation-job';

export type CreateFindingVars = {
  caseId: string;
  fileUrl: string;
  detectionType?: DetectionType;
  /** File-info status at click time. REVIEWED means this flag is a re-flag. */
  baselineFindingStatus?: string | null;
};

/** An active finding was already stored. This POST did not create another one. */
export type ExistingActiveFinding = { existingActiveFinding: true };

export type CreateFindingResult = ContentModerationFinding | ExistingActiveFinding;

function isExistingActiveFinding(result: CreateFindingResult): result is ExistingActiveFinding {
  return 'existingActiveFinding' in result && result.existingActiveFinding === true;
}

function isActiveFindingStatus(status?: string | null): status is 'FLAGGED' | 'CONFIRMED' {
  return status === 'FLAGGED' || status === 'CONFIRMED';
}

export function useCreateContentModerationFinding() {
  const { cellId, accessToken } = useCellServiceSession();
  const resolveFolderName = useContentModerationFolderNameResolver();
  const queryClient = useQueryClient();

  return useMutation<CreateFindingResult, Error, CreateFindingVars>({
    mutationFn: async ({ caseId, fileUrl, detectionType, baselineFindingStatus }) => {
      const folderName = resolveFolderName(caseId);
      if (!folderName) {
        // Refuse rather than fall back to the display name: that writes a row
        // the ingest-keyed findings list will never show, so the analyst sees
        // their flag succeed and then vanish.
        throw new Error('Could not resolve this case folder. Please try again.');
      }
      try {
        return await createContentModerationFinding({
          cellId: cellId as string,
          accessToken: accessToken as string,
          caseId: folderName,
          fileUrl,
          detectionType,
        });
      } catch (error) {
        if (!isExistingObfuscationConflict(error)) throw error;
        // 409 writes nothing. Follow an already-active finding. Do not PUT
        // status: that endpoint is the verifier Confirm action, so a case
        // user is rejected and a verifier verdict is not a new user flag.
        if (isActiveFindingStatus(baselineFindingStatus)) {
          return { existingActiveFinding: true };
        }
        const file = await fetchProvenanceFileStatus({
          cellId: cellId as string,
          accessToken: accessToken as string,
          s3Url: fileUrl,
        }).catch(() => null);
        if (isActiveFindingStatus(file?.contentModeration?.findingStatus)) {
          return { existingActiveFinding: true };
        }
        throw new ProvenanceApiError(FLAG_NOT_SAVED_EXISTING_OBFUSCATION, 409);
      }
    },
    onSuccess: (result, { caseId, fileUrl, baselineFindingStatus }) => {
      if (!isExistingActiveFinding(result)) {
        markManualObfuscationInProgress(fileUrl, baselineFindingStatus === 'REVIEWED');
      }
      invalidateFileInfoCache(fileUrl);
      // Same segment the mutation just wrote under, so the cache keys line up.
      const folderName = resolveFolderName(caseId);
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'findings', cellId, folderName],
      });
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'summary', cellId, folderName],
      });
      queryClient.invalidateQueries({
        queryKey: [...provenanceKeys.contentModeration(), 'user-flagged'],
      });
      queryClient.invalidateQueries({ queryKey: provenanceKeys.fileInfo() });
      queryClient.invalidateQueries({ queryKey: provenanceKeys.tree() });
      queryClient.invalidateQueries({ queryKey: provenanceKeys.files() });
      queryClient.invalidateQueries({ queryKey: auditKeys.csamAudit() });
    },
  });
}
